<script setup>
import { ref, computed, toRef } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useTemplateUsage } from 'dashboard/composables/useTemplateUsage';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const isRefreshing = ref(false);
const selectedCategory = ref('');

const { recentlyUsedIds, recordUsage } = useTemplateUsage(
  'recently_used_whatsapp_templates'
);

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

// WhatsApp templates already carry a real `category` from the Meta API
// (MARKETING/UTILITY/AUTHENTICATION) - unlike canned responses, no
// derivation needed here, just surfacing what's already on the object.
const availableCategories = computed(() => {
  const categories = new Set(
    whatsAppTemplateMessages.value
      .map(template => template.category)
      .filter(Boolean)
  );
  return [...categories].sort();
});

const filteredTemplateMessages = computed(() => {
  let templates = whatsAppTemplateMessages.value;
  if (selectedCategory.value) {
    templates = templates.filter(
      template => template.category === selectedCategory.value
    );
  }
  if (query.value) {
    templates = templates.filter(template =>
      template.name.toLowerCase().includes(query.value.toLowerCase())
    );
  }
  return templates;
});

// WhatsApp templates from the Meta API have no `id` field (confirmed
// against real data - only name/status/category/language/components);
// `name` is the natural unique key (Meta doesn't allow two approved
// templates with the same name+language) and is already what search
// filters on above.
// While neither searching nor filtering, surface recently-used templates
// first - same rationale as the canned-response slash-menu.
const orderedTemplateMessages = computed(() => {
  if (query.value || selectedCategory.value)
    return filteredTemplateMessages.value;

  const recentTemplates = recentlyUsedIds.value
    .map(name =>
      filteredTemplateMessages.value.find(template => template.name === name)
    )
    .filter(Boolean);
  if (!recentTemplates.length) return filteredTemplateMessages.value;

  const recentNames = new Set(recentTemplates.map(template => template.name));
  return [
    ...recentTemplates,
    ...filteredTemplateMessages.value.filter(
      template => !recentNames.has(template.name)
    ),
  ];
});

const selectTemplate = template => {
  recordUsage(template.name);
  emit('onSelect', template);
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>
    <div
      v-if="availableCategories.length > 1"
      class="flex flex-wrap gap-1 mb-2.5"
    >
      <button
        class="px-2 py-1 text-xs rounded-lg border-0 cursor-pointer"
        :class="
          !selectedCategory
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4'
        "
        @click="selectedCategory = ''"
      >
        {{ t('WHATSAPP_TEMPLATES.PICKER.ALL_CATEGORIES') }}
      </button>
      <button
        v-for="category in availableCategories"
        :key="category"
        class="px-2 py-1 text-xs rounded-lg border-0 cursor-pointer"
        :class="
          selectedCategory === category
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4'
        "
        @click="selectedCategory = category"
      >
        {{ category }}
      </button>
    </div>
    <div
      class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[18.75rem] overflow-y-auto p-2.5"
    >
      <div
        v-for="(template, i) in orderedTemplateMessages"
        :key="template.name"
      >
        <button
          class="block p-2.5 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2"
          @click="selectTemplate(template)"
        >
          <div class="flex justify-between items-center mb-2.5">
            <p class="text-sm">
              {{ template.name }}
            </p>
            <div class="flex gap-1 items-center">
              <span
                class="inline-block px-2 py-1 text-xs leading-none rounded-lg cursor-default bg-n-slate-3 text-n-slate-12"
              >
                {{ template.category }}
              </span>
              <span
                class="inline-block px-2 py-1 text-xs leading-none rounded-lg cursor-default bg-n-slate-3 text-n-slate-12"
              >
                {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }}:
                {{ template.language }}
              </span>
            </div>
          </div>
          <TemplatePreview
            :template="template"
            :variables="{}"
            :platform="PLATFORMS.WHATSAPP"
          />
        </button>
        <hr
          v-if="i != orderedTemplateMessages.length - 1"
          :key="`hr-${i}`"
          class="border-b border-solid border-n-weak my-2.5 mx-auto max-w-[95%]"
        />
      </div>
      <div v-if="!orderedTemplateMessages.length" class="py-8 text-center">
        <div v-if="query && whatsAppTemplateMessages.length">
          <p>
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            <strong>{{ query }}</strong>
          </p>
        </div>
        <div v-else-if="!whatsAppTemplateMessages.length" class="space-y-4">
          <p class="text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
